import React, { useEffect, useState, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Album, AlbumsData, LinkType } from '../types'
import {
    API_ORIGIN,
    CONTENT_ORIGIN,
    storeButtonLabels,
    storeOrder,
    SKIP_PLATFORMS_DISPLAY,
} from '../config'
import {
    AlbumCover,
    CallToAction,
    ShowMoreButton,
    StoreCard,
    StoreList,
    StoreLogo,
    Title,
    Container,
} from './Item'
import { useConfig } from '../hooks/useConfig'
import { useTracking } from '../hooks/useTracking'
import { usePageMeta } from '../hooks/usePageMeta'

const LatestReleaseContainer = styled.div`
    width: 100%;
    max-width: 560px;
    margin-bottom: 34px;
    text-align: center;
    padding: 24px 16px 18px;
    box-sizing: border-box;
    border-radius: 22px;
    border: 1px solid ${(props) => props.theme.border};
    background: linear-gradient(
        160deg,
        rgba(255, 255, 255, 0.05),
        rgba(255, 255, 255, 0.015)
    );
    box-shadow: 0 28px 60px rgba(2, 5, 12, 0.45);
`

const ArtistLine = styled.p`
    margin: 0 0 16px;
    color: ${(props) => props.theme.secondaryText};
`

const SectionTitle = styled.h2`
    margin: 6px 0 14px;
    font-size: clamp(1.25rem, 2.3vw, 1.7rem);
    font-weight: 650;
    letter-spacing: 0.01em;
`

const ReleasesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 0 0 20px;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    box-sizing: border-box;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const CoverLink = React.memo(styled(Link)`
    text-decoration: none;
    margin: 0;
    padding: 0;
    display: block;
    line-height: 0;
`)

const CoverImage = styled.img`
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 14px;
    border: 1px solid ${(props) => props.theme.border};
    box-shadow: 0 10px 24px rgba(2, 5, 10, 0.35);
    transition:
        transform 0.3s ease,
        box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 20px 36px rgba(2, 5, 10, 0.5);
    }
`

const MerchCoverImage = styled(CoverImage)`
    border: none;
    box-shadow: none;
`

const MerchContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    padding: 0 0 20px;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
    box-sizing: border-box;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const MerchItem = styled.a`
    text-decoration: none;
    display: block;
    line-height: 0;
    transition: transform 0.3s ease;
    &:hover {
        transform: translateY(-2px) scale(1.02);
    }
`

const VstTeaserCard = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 14px;
    padding: 12px 14px;
    width: 100%;
    max-width: 380px;
    text-decoration: none;
    color: ${(props) => props.theme.text};
    box-shadow: 0 10px 24px rgba(3, 5, 9, 0.44);
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        background 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 16px 34px rgba(3, 5, 9, 0.6);
        background: rgba(255, 255, 255, 0.08);
    }
`

const GET_RELEASES = gql`
    query GetReleases($domain: String!, $limit: Int!, $start: Int!) {
        albums(
            filters: { artists: { page: { domain: { eq: $domain } } } }
            sort: ["release_date:desc"]
            pagination: { limit: $limit, start: $start }
        ) {
            title
            release_date
            documentId
            links {
                documentId
                url
                platform
            }
            cover {
                formats
                url
            }
            artists {
                name
            }
        }
    }
`

type ReleasesType = {
    domain: string
}

const VST_ENABLED_DOMAINS = new Set(['gssnhr.com', 'tole.live'])

const Releases = ({ domain }: ReleasesType) => {
    const [albums, setAlbums] = useState<Album[]>([])
    const [latestReleasedAlbum, setLatestReleasedAlbum] =
        useState<Album | null>(null)
    const [hasMore, setHasMore] = useState<boolean>(true)
    const [offset, setOffset] = useState<number>(4)
    const { config } = useConfig(domain)
    const trackLink = useTracking()

    const [merch, setMerch] = useState<
        { title: string; url: string; image: string }[]
    >([])
    const [fetchReleases, { data, loading }] = useLazyQuery<AlbumsData>(
        GET_RELEASES,
        { fetchPolicy: 'network-only' }
    )

    const loadAlbums = useCallback(
        (start: number, limit: number) => {
            fetchReleases({ variables: { domain, limit, start } })
        },
        [domain, fetchReleases]
    )

    useEffect(() => {
        if (domain) loadAlbums(0, 4)
    }, [domain, loadAlbums])

    useEffect(() => {
        if (data) {
            const newAlbums = data.albums || []
            if (newAlbums.length > 0) {
                if (!latestReleasedAlbum) {
                    // Set the latest release as the first album
                    setLatestReleasedAlbum(newAlbums[0])
                    setAlbums(newAlbums.slice(1)) // Skip the latest release for the grid
                } else {
                    setAlbums((prev) => [
                        ...prev,
                        ...newAlbums.filter(
                            (album) =>
                                !prev.some(
                                    (existing) =>
                                        existing.documentId === album.documentId
                                )
                        ),
                    ])
                }
                setHasMore(newAlbums.length === 4 || newAlbums.length === 12) // Check if the batch is full
            } else {
                setHasMore(false) // No more albums
            }
        }
    }, [data])

    useEffect(() => {
        if (!config?.shopify_collection) {
            console.warn('No Shopify collection specified in config')
            return
        }

        const merchUrl = `${API_ORIGIN}/shopify/collection/${config.shopify_collection}`
        console.log(`Fetching merch from: ${merchUrl}`)

        fetch(merchUrl)
            .then((res) => {
                console.log(
                    `Received response: ${res.status} ${res.statusText}`
                )
                return res.json()
            })
            .then((data) => {
                console.log('Fetched merch data:', data)
                setMerch(data)
            })
            .catch((error) => {
                console.error('Error fetching merch:', error)
                setMerch([])
            })
    }, [config])

    const handleLinkClick = (link: LinkType) => {
        trackLink(link.documentId, domain)
    }

    usePageMeta(
        config &&
            latestReleasedAlbum &&
            `${config?.name} | ${latestReleasedAlbum?.title}`,
        null
    )

    const handleLoadMore = useCallback(() => {
        loadAlbums(offset, 12)
        setOffset((prev) => prev + 12)
    }, [offset, loadAlbums])

    const sortedLinks = useMemo(() => {
        return [...(latestReleasedAlbum?.links || [])].sort(
            (a, b) =>
                storeOrder.indexOf(a.platform) - storeOrder.indexOf(b.platform)
        )
    }, [latestReleasedAlbum])

    return (
        <Container>
            {latestReleasedAlbum && (
                <LatestReleaseContainer>
                    <SectionTitle>Latest Release</SectionTitle>
                    <Title>{latestReleasedAlbum.title}</Title>
                    <ArtistLine>
                        {latestReleasedAlbum.artists
                            ?.map((artist) => artist.name)
                            .join(', ')}
                    </ArtistLine>
                    <AlbumCover
                        src={`${CONTENT_ORIGIN}${latestReleasedAlbum.cover?.url}`}
                        alt={`Cover art of ${latestReleasedAlbum.title || 'Unknown Album'}`}
                    />
                    <StoreList>
                        {sortedLinks
                            .filter(
                                (link) =>
                                    !SKIP_PLATFORMS_DISPLAY.has(
                                        link.platform.toLowerCase()
                                    )
                            )
                            .map((link) => (
                                <StoreCard
                                    key={link.documentId}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => handleLinkClick(link)}
                                >
                                    <StoreLogo
                                        src={`/images/logo_${link.platform.toLowerCase()}_ondark.svg`}
                                        alt={`${link.platform} logo`}
                                    />
                                    <CallToAction>
                                        {storeButtonLabels[link.platform] ||
                                            'Besuchen'}
                                    </CallToAction>
                                </StoreCard>
                            ))}
                    </StoreList>
                </LatestReleaseContainer>
            )}
            <SectionTitle>Weitere Releases</SectionTitle>
            <ReleasesContainer>
                {albums.map((release) => (
                    <CoverLink
                        key={release.documentId}
                        to={`/release/${release.documentId}`}
                    >
                        <CoverImage
                            src={`${CONTENT_ORIGIN}${release.cover?.formats?.small?.url}`}
                            alt={`Cover art of ${release.title || 'Unknown Album'}`}
                            loading="lazy"
                        />
                    </CoverLink>
                ))}
            </ReleasesContainer>
            {hasMore && !loading && (
                <ShowMoreButton onClick={handleLoadMore}>
                    Mehr anzeigen
                </ShowMoreButton>
            )}
            {VST_ENABLED_DOMAINS.has(domain) && (
                <>
                    <SectionTitle>Free Plugin</SectionTitle>
                    <StoreList style={{ marginBottom: '24px' }}>
                        <VstTeaserCard to="/free-vst">
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '3px',
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    WetCat — Free VST
                                </span>
                                <span
                                    style={{
                                        fontSize: '0.72rem',
                                        opacity: 0.55,
                                    }}
                                >
                                    macOS · Windows coming soon
                                </span>
                            </div>
                            <CallToAction>Get it free</CallToAction>
                        </VstTeaserCard>
                    </StoreList>
                </>
            )}
            {merch.length >= 1 && (
                <>
                    <SectionTitle>Merch</SectionTitle>
                    <MerchContainer>
                        {merch.map((item, index) => (
                            <MerchItem
                                key={index}
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <MerchCoverImage
                                    src={item.image}
                                    alt={item.title}
                                />
                            </MerchItem>
                        ))}
                    </MerchContainer>
                </>
            )}
            {loading && <p>Loading...</p>}
        </Container>
    )
}

export default Releases
